import React, { useState } from "react";
import { cn } from "../../lib/utils";
import { MAX_CONTENT_IMAGES } from "../../lib/contentImages";

/**
 * Small thumbnail strip for selected images. About six fit per row.
 * Drag a thumbnail onto another to reorder. A + tile adds more.
 *
 * @param {Object} props
 * @param {Array<{id: string, preview: string}>} props.items
 * @param {(fromId: string, toId: string) => void} props.onReorder
 * @param {(id: string) => void} props.onRemove
 * @param {() => void} props.onAdd
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.showAddWhenEmpty]
 * @param {boolean} [props.allowAdd]
 * @param {number} [props.maxImages]
 * @returns {JSX.Element | null}
 */
export default function ImagePreviewStrip({
  items,
  onReorder,
  onRemove,
  onAdd,
  disabled = false,
  showAddWhenEmpty = false,
  allowAdd = true,
  maxImages = MAX_CONTENT_IMAGES,
}) {
  const [dragId, setDragId] = useState(null);
  const limit = Number.isFinite(maxImages) && maxImages > 0 ? maxImages : MAX_CONTENT_IMAGES;
  const atMax = items.length >= limit;
  const showAdd = allowAdd && !atMax && (items.length > 0 || showAddWhenEmpty);

  if (!items.length && !showAddWhenEmpty) {
    return null;
  }

  /**
   * @param {string} targetId
   */
  function dropOn(targetId) {
    if (!dragId || dragId === targetId || disabled) return;
    onReorder(dragId, targetId);
    setDragId(null);
  }

  return (
    <div className="grid grid-cols-6 gap-2">
      {items.map((item) => (
        <div
          key={item.id}
          draggable={!disabled}
          onDragStart={() => setDragId(item.id)}
          onDragEnd={() => setDragId(null)}
          onDragOver={(event) => {
            event.preventDefault();
          }}
          onDrop={(event) => {
            event.preventDefault();
            dropOn(item.id);
          }}
          className={cn(
            "relative aspect-square cursor-grab overflow-hidden rounded-md active:cursor-grabbing",
            dragId === item.id && "opacity-50"
          )}
        >
          <img
            src={item.preview}
            alt=""
            draggable={false}
            className="h-full w-full rounded-md border border-ui-border object-cover"
          />
          <button
            type="button"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              onRemove(item.id);
            }}
            disabled={disabled}
            className="absolute right-0.5 top-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-ui-overlay text-xs leading-none text-text-on-accent hover:bg-delete disabled:opacity-50"
            aria-label="Remove image"
          >
            ×
          </button>
        </div>
      ))}
      {showAdd ? (
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          aria-label="Add images"
          className="flex aspect-square items-center justify-center rounded-md border border-dashed border-ui-border bg-ui-background text-text-secondary transition hover:bg-highlight hover:text-text-primary disabled:opacity-50"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
          </svg>
        </button>
      ) : null}
    </div>
  );
}
